import React from 'react';
import type { ApolloError } from '@apollo/client';
import * as Headless from '@headlessui/react';
import { PaperAirplaneIcon } from '@heroicons/react/24/outline';
import { Button } from '@oysterjs/ui/button';
import { DataTable } from '@oysterjs/ui/data-table';
import { Label } from '@oysterjs/ui/fieldset';
import {
  type ActiveFilter,
  SearchFilterInput,
  useFilterHashSync,
  useFiltersFromHash
} from '@oysterjs/ui/search-filter';
import { Spinner } from '@oysterjs/ui/spinner';
import { Switch, SwitchField } from '@oysterjs/ui/switch';
import { Dialog, DialogActions, DialogBody, DialogTitle } from '@oysterjs/ui/dialog';
import { messageFromError } from '@oysterjs/core/graphql/errors';
import { client } from '~/api/client';
import {
  type OutboundEmail,
  type OutboundEmailAmsLogFieldsFragment,
  type OutboundEmailFieldsFragment,
  OutboxStatus as OutboxStatusFilterValue,
  useEmailBulkSendLazyQuery,
  useEmailBulkSendQuery,
  SentEmailAmsLogDocument,
  type SentEmailAmsLogQuery,
  type SentEmailAmsLogQueryVariables,
  useEmailBulkSendsQuery,
  useMailboxAccountSharingQuery,
  useMailboxAccountsQuery,
  useSearchMailboxDraftsQuery,
  useSearchOutboundEmailsQuery,
  useSearchOutboxArtifactsQuery
} from '~/api/graphql';
import { PushEmailToAms } from '~/components/ams-email-log/push-email-to-ams';
import { useAgencyUsers, useCurrentAgencyUserId } from '~/components/current-user';
import { InlineNotice } from '~/components/inline-notice';
import { FEATURE_FLAGS, useFlag } from '~/components/feature-flags';
import { EMAIL_BULK_SENDS_LIMIT } from '~/api/data/email-bulk-send/queries';
import { LONGEST_TIMER_DELAY_MS } from '~/lib/scheduled-send';
import { useConnectionPagination } from '~/components/use-connection-pagination';
import {
  type OutboxFilterType,
  createOutboxFilterDefinitions,
  outboxFilterBulkSendId,
  outboxFilterInput,
  outboxFilterNamesClosedStatus,
  outboxFilterStatuses
} from '~/api/data/mailbox/outbox-filters';
import { outboundEmailSortToSortInput } from '~/components/mailbox/outbound-emails-columns';
import { pushableOutboundEmail } from '~/components/mailbox/outbox-ams-cell';
import {
  type OutboxAmsColumn,
  getOutboxColumns,
  openableMailboxAccountIds,
  outboxRowArtifactHref,
  outboxUnconfirmedRetryWarning
} from '~/components/mailbox/outbox-columns';
import {
  useMailboxDraftReconciliation,
  useReconcileMailboxDrafts
} from '~/api/data/mailbox/outbox-reconcile';
import {
  type OutboxBulkSend,
  groupOutboxRowsUnderBulkSends,
  outboxBatches,
  isBulkSendDrafting,
  outboxRowsWithBulkSends
} from '~/components/mailbox/outbox-bulk-send-rows';
import { useMissingOutboxBulkSends } from '~/components/mailbox/use-missing-outbox-bulk-sends';
import { BatchProgressLoadFailure } from './bulk-draft-sent';
import { amsLogPollingRemainingMs } from '~/components/ams-email-log/polling';
import { useAmsLoggingEnabled } from '~/components/use-ams-logging-enabled';
import { integrationName } from '~/lib/integrations';
import { OutboxScheduleBanners, outboxScheduleBanners } from './outbox-schedule-banners';
import { useBatchProgressPolling } from './use-batch-progress-polling';
import {
  OutboxRow,
  OutboxSourceWindow,
  applyOutboxDraftPatches,
  buildOutboxRows,
  hasUnresolvedOutboxRows,
  nextScheduledSendTimeMs,
  outboxRowIsClosed,
  outboxSourceFilters,
  outboxSourcesIncomplete,
  outboxWatermarkMs,
  reconcilableDraftIds,
  visibleOutboxRows
} from '~/components/mailbox/outbox-rows';

const OUTBOX_PAGE_SIZE = 50;

/**
 * How often an on-screen SENDING row is re-examined. Two things change without
 * any input from the user: the row resolves, and — if it does not — it crosses
 * the age at which it should stop reading as "in flight".
 */
const OUTBOX_POLL_MS = 15_000;

const NO_BULK_SENDS: OutboxBulkSend[] = [];

const AMS_LOG_FIELD: keyof OutboundEmail = 'amsLog';

type ErrorPath = readonly (string | number)[] | undefined;

const isAmsLogErrorPath = (path: ErrorPath) => !!path?.includes(AMS_LOG_FIELD);

/**
 * Separates errors reading the sends' AMS logs from the sends' own: the sends still
 * loaded, so only the AMS column says it couldn't read them.
 */
const sendsErrorOf = (error: ApolloError | undefined) => {
  const isOnlyAmsLogErrors =
    !!error &&
    !error.networkError &&
    error.graphQLErrors.every((graphQLError) => isAmsLogErrorPath(graphQLError.path));
  return isOnlyAmsLogErrors ? undefined : error;
};

/** The sends whose AMS log a read couldn't load, by the edge index in each error's path. */
const unreadAmsLogEmailIds = (
  errors: readonly { path?: ErrorPath }[] | undefined,
  edges: readonly { node: { id: string } }[] | undefined
): string[] =>
  (errors ?? []).flatMap(({ path }) => {
    const edgeIndex = path?.find((part) => typeof part === 'number');
    const emailId =
      isAmsLogErrorPath(path) && edgeIndex !== undefined ? edges?.[edgeIndex]?.node.id : undefined;
    return emailId ? [emailId] : [];
  });

type PushedOutboundEmail = {
  email: OutboundEmailFieldsFragment;
  amsLog: OutboundEmailAmsLogFieldsFragment;
};

/**
 * Reads a pushed email's AMS log again into the cache, which updates its row. A failed
 * read leaves the row as it was until the page reads it again.
 */
const rereadAmsLog = (workspaceArtifactId: string) =>
  client
    .query<SentEmailAmsLogQuery, SentEmailAmsLogQueryVariables>({
      query: SentEmailAmsLogDocument,
      variables: { workspaceArtifactId },
      fetchPolicy: 'network-only',
      // A read that errored mustn't write its null amsLog over the row's good one.
      errorPolicy: 'none'
    })
    .catch(() => undefined);

/**
 * How far back a source has been read, for the merge. A source whose first
 * page has not landed reports no floor at all and is not exhausted, which is
 * what holds the whole list back until it does: rendering the other source's
 * rows first would place them against a list that is about to gain rows above
 * them.
 *
 * A source that failed counts as exhausted, and is marked failed as well: the
 * merge cannot tell "nothing more is coming" from "you have seen everything",
 * and everything downstream of it has to.
 */
const sourceWindow = <T,>(opts: {
  rows: T[];
  // Artifacts are placed by when they were composed, ledger rows by when they
  // were written.
  sortAt: (row: T) => Date | string;
  settled: boolean;
  hasNextPage: boolean;
  failed: boolean;
}): OutboxSourceWindow => {
  const { rows, sortAt, settled, hasNextPage, failed } = opts;
  const oldest = rows.length > 0 ? new Date(sortAt(rows[rows.length - 1])) : null;
  return { oldestFetchedAt: oldest, exhausted: failed || (settled && !hasNextPage), failed };
};

export const OutboxPage = () => {
  const agencyUsers = useAgencyUsers();
  const currentUserId = useCurrentAgencyUserId();

  const [includeClosed, setIncludeClosed] = React.useState(false);
  const [nowMs, setNowMs] = React.useState(() => Date.now());
  // The row whose send Cara could not account for, held while the reader
  // decides.
  const [retryUnconfirmed, setRetryUnconfirmed] = React.useState<OutboxRow | null>(null);
  const scheduledSendFlagEnabled = useFlag(FEATURE_FLAGS.MAILBOX_SCHEDULED_SEND);
  const isAmsLoggingEnabled = useAmsLoggingEnabled();
  const [pushing, setPushing] = React.useState<PushedOutboundEmail | null>(null);
  const isBulkSendEnabled = useFlag(FEATURE_FLAGS.MAILBOX_BULK_SEND);

  // Feeds the reconnect banner over scheduled emails and names each mailbox's
  // client ("Reconnect Outlook"). A failed read only loses the banner.
  const { data: mailboxAccounts } = useMailboxAccountsQuery();
  const filterDefinitions = React.useMemo(
    () =>
      createOutboxFilterDefinitions(
        agencyUsers,
        currentUserId,
        scheduledSendFlagEnabled,
        isBulkSendEnabled
      ),
    [agencyUsers, currentUserId, scheduledSendFlagEnabled, isBulkSendEnabled]
  );
  const initialFilters = useFiltersFromHash(filterDefinitions);
  const [activeFilters, setActiveFilters] =
    React.useState<ActiveFilter<OutboxFilterType>[]>(initialFilters);
  useFilterHashSync(activeFilters);
  const filtered = activeFilters.length > 0;
  const filteredBulkSendId = outboxFilterBulkSendId(activeFilters);
  const bulkDraftsQuery = useEmailBulkSendsQuery({
    variables: { first: EMAIL_BULK_SENDS_LIMIT, unsentOnly: true },
    skip: !isBulkSendEnabled
  });
  // Every recent bulk email, sent or not, for its own row and the batch each sent email is in.
  const recentBulkSendsQuery = useEmailBulkSendsQuery({
    variables: { first: EMAIL_BULK_SENDS_LIMIT },
    skip: !isBulkSendEnabled
  });
  const bulkDrafts = bulkDraftsQuery.data?.emailBulkSends ?? NO_BULK_SENDS;
  const recentBulkSends = recentBulkSendsQuery.data?.emailBulkSends ?? NO_BULK_SENDS;
  const { startPolling: startPollingBulkDrafts, stopPolling: stopPollingBulkDrafts } =
    bulkDraftsQuery;
  // Read again while Cara drafts, so a bulk draft's row counts up.
  const isAnyBulkDraftDrafting = bulkDrafts.some(isBulkSendDrafting);
  React.useEffect(() => {
    if (!isAnyBulkDraftDrafting) {
      return;
    }
    startPollingBulkDrafts(OUTBOX_POLL_MS);
    return stopPollingBulkDrafts;
  }, [isAnyBulkDraftDrafting, startPollingBulkDrafts, stopPollingBulkDrafts]);
  const batches = React.useMemo(() => outboxBatches(recentBulkSends), [recentBulkSends]);
  const bulkSendsError = bulkDraftsQuery.error ?? recentBulkSendsQuery.error;
  const bulkSends = React.useMemo(
    () => [...bulkDrafts, ...recentBulkSends],
    [bulkDrafts, recentBulkSends]
  );
  // Read on its own, as it may be older than the recent bulk emails.
  const filteredBatchQuery = useEmailBulkSendQuery({
    variables: { id: filteredBulkSendId ?? '' },
    skip: !isBulkSendEnabled || !filteredBulkSendId
  });
  const filteredBatch = filteredBatchQuery.data?.emailBulkSend;

  // Not the polling clock, which ticks: see the `nowMs` note on
  // outboxFilterInput.
  const outboxFilter = React.useMemo(
    () => outboxFilterInput(activeFilters, { nowMs: Date.now() }),
    [activeFilters]
  );

  const filters = React.useMemo(
    () => outboxSourceFilters({ outbox: outboxFilter }),
    [outboxFilter]
  );
  const artifactsFilter = filters.artifacts;
  const draftsFilter = filters.drafts;
  const outboundFilter = filters.outbound;

  // The merge needs every source in the same order, and two of the three offer
  // only one: newest first by creation.
  const outboundSort = React.useMemo(() => outboundEmailSortToSortInput('created', 'desc'), []);

  const artifacts = useSearchOutboxArtifactsQuery({
    variables: { first: OUTBOX_PAGE_SIZE, filter: artifactsFilter },
    notifyOnNetworkStatusChange: true
  });
  const drafts = useSearchMailboxDraftsQuery({
    variables: { first: OUTBOX_PAGE_SIZE, filter: draftsFilter },
    notifyOnNetworkStatusChange: true
  });
  const outbound = useSearchOutboundEmailsQuery({
    variables: { first: OUTBOX_PAGE_SIZE, filter: outboundFilter, sort: outboundSort },
    notifyOnNetworkStatusChange: true
  });

  const sendsError = React.useMemo(() => sendsErrorOf(outbound.error), [outbound.error]);
  // A Load more page's AMS errors aren't kept on the query, so they're kept here.
  const [unreadOnLaterPages, setUnreadOnLaterPages] = React.useState<ReadonlySet<string>>(
    () => new Set()
  );

  const fetchMoreArtifacts = React.useCallback(
    (after: string) =>
      artifacts.fetchMore({
        variables: { after, first: OUTBOX_PAGE_SIZE, filter: artifactsFilter }
      }),
    [artifacts, artifactsFilter]
  );
  const fetchMoreDrafts = React.useCallback(
    (after: string) =>
      drafts.fetchMore({ variables: { after, first: OUTBOX_PAGE_SIZE, filter: draftsFilter } }),
    [drafts, draftsFilter]
  );
  const fetchMoreOutbound = React.useCallback(
    (after: string) =>
      outbound
        .fetchMore({
          variables: { after, first: OUTBOX_PAGE_SIZE, filter: outboundFilter, sort: outboundSort }
        })
        .then((page) => {
          const unread = unreadAmsLogEmailIds(page.errors, page.data?.searchOutboundEmails.edges);
          if (unread.length > 0) {
            setUnreadOnLaterPages((emailIds) => new Set([...emailIds, ...unread]));
          }
          return page;
        }),
    [outbound, outboundFilter, outboundSort]
  );

  const artifactPages = useConnectionPagination({
    connection: artifacts.data?.searchCaraWorkspaceArtifacts,
    loading: artifacts.loading,
    fetchMore: fetchMoreArtifacts
  });
  const draftPages = useConnectionPagination({
    connection: drafts.data?.searchMailboxDrafts,
    loading: drafts.loading,
    fetchMore: fetchMoreDrafts
  });
  const outboundPages = useConnectionPagination({
    connection: outbound.data?.searchOutboundEmails,
    loading: outbound.loading,
    fetchMore: fetchMoreOutbound
  });

  const windows = React.useMemo<OutboxSourceWindow[]>(
    () => [
      sourceWindow({
        rows: artifactPages.rows,
        sortAt: (artifact) => artifact.composedAt,
        settled: !!artifacts.data || !!artifacts.error,
        hasNextPage: artifactPages.hasNextPage,
        failed: !!artifacts.error
      }),
      sourceWindow({
        rows: draftPages.rows,
        sortAt: (draft) => draft.createdAt,
        settled: !!drafts.data || !!drafts.error,
        hasNextPage: draftPages.hasNextPage,
        failed: !!drafts.error
      }),
      sourceWindow({
        rows: outboundPages.rows,
        sortAt: (email) => email.createdAt,
        settled: !!outbound.data || !!sendsError,
        hasNextPage: outboundPages.hasNextPage,
        failed: !!sendsError
      })
    ],
    [
      artifactPages.rows,
      artifactPages.hasNextPage,
      artifacts.data,
      artifacts.error,
      draftPages.rows,
      draftPages.hasNextPage,
      drafts.data,
      drafts.error,
      outboundPages.rows,
      outboundPages.hasNextPage,
      outbound.data,
      sendsError
    ]
  );

  const watermarkMs = React.useMemo(() => outboxWatermarkMs(windows), [windows]);

  // Which drafts to check is asked of the ledger rather than of the reconciled
  // rows, so that the answers cannot decide what gets asked next.
  const reconcilable = React.useMemo(
    () =>
      reconcilableDraftIds(
        visibleOutboxRows(
          buildOutboxRows({
            artifacts: artifactPages.rows,
            drafts: draftPages.rows,
            outboundEmails: outboundPages.rows
          }),
          watermarkMs
        ),
        nowMs
      ),
    [artifactPages.rows, draftPages.rows, outboundPages.rows, watermarkMs, nowMs]
  );
  const reconcile = useReconcileMailboxDrafts();
  const reconciliation = useMailboxDraftReconciliation({ draftIds: reconcilable, reconcile });

  // Not a filter: the list is a window on the most recent email, and this
  // widens what that window shows rather than narrowing what it is a window on.
  const showClosed = includeClosed || outboxFilterNamesClosedStatus(activeFilters);

  // Closed rows are dropped here rather than left out of the query: the draft
  // ledger is always read whole, because a row whose draft is missing reads as
  // an email that never reached a mailbox at all.
  const emailRows = React.useMemo(
    () =>
      buildOutboxRows({
        artifacts: artifactPages.rows,
        drafts: applyOutboxDraftPatches(draftPages.rows, reconciliation.patches),
        outboundEmails: outboundPages.rows
      }),
    [artifactPages.rows, draftPages.rows, outboundPages.rows, reconciliation.patches]
  );
  // A filtered list stays flat, so it needs no bulk email beyond the one it's filtered to.
  const olderBulkSends = useMissingOutboxBulkSends(
    emailRows,
    bulkSends,
    filtered || !isBulkSendEnabled
  );
  const rows = React.useMemo(() => {
    const withBulkSends = outboxRowsWithBulkSends(emailRows, [...bulkSends, ...olderBulkSends], {
      isFiltered: filtered,
      filteredBatch
    });
    // A filtered list stays in date order: it may hold a batch's emails without its row.
    const placed = filtered
      ? visibleOutboxRows(withBulkSends, watermarkMs)
      : groupOutboxRowsUnderBulkSends(withBulkSends, watermarkMs);
    return showClosed ? placed : placed.filter((row) => !outboxRowIsClosed(row, nowMs));
  }, [
    emailRows,
    bulkSends,
    olderBulkSends,
    filtered,
    filteredBatch,
    watermarkMs,
    showClosed,
    nowMs
  ]);

  const sentBulkSends = React.useMemo(
    () => rows.flatMap((row) => (row.bulkSend?.sentAt ? [row.bulkSend] : [])),
    [rows]
  );

  // A bulk draft can be the only row, so "nothing here" waits for the bulk emails too.
  const loading =
    artifacts.loading ||
    drafts.loading ||
    outbound.loading ||
    bulkDraftsQuery.loading ||
    recentBulkSendsQuery.loading;
  const error = artifacts.error ?? drafts.error ?? sendsError;
  // A bulk draft can be the only row, so an unread list of them can't be called empty.
  const emptyListError = error ?? bulkDraftsQuery.error;

  // Re-asks every source at once. A rejection lands back on the same error
  // state this was rendered from, so there is nothing further to do with it.
  const retry = React.useCallback(() => {
    void Promise.allSettled([
      artifacts.refetch(),
      drafts.refetch(),
      outbound.refetch(),
      ...(isBulkSendEnabled ? [bulkDraftsQuery.refetch(), recentBulkSendsQuery.refetch()] : [])
    ]);
  }, [artifacts, drafts, outbound, isBulkSendEnabled, bulkDraftsQuery, recentBulkSendsQuery]);

  // Which ledgers could not be read, so that a row does not report a stage it
  // may have left. The artifact ledger is not among them: it sits under both
  // of the others, so losing it costs whole rows rather than making the rows
  // that did arrive say the wrong thing.
  const unreadSources = React.useMemo(
    () => ({ drafts: !!drafts.error, outbound: !!sendsError }),
    [drafts.error, sendsError]
  );
  // Unread bulk drafts lose their rows; unread recent bulk emails, their sent bulk rows.
  const incomplete = outboxSourcesIncomplete(windows) || !!bulkSendsError;
  const hasNextPage = windows.some((window) => !window.exhausted);
  // Every source reads its next page: paging only the one the watermark sits on lowers it just
  // to the next source's oldest row, which can show a single row more. Read together, the
  // source left holding the watermark shows its whole page, or every source runs out.
  const loadMore = React.useCallback(() => {
    artifactPages.loadMore();
    draftPages.loadMore();
    outboundPages.loadMore();
  }, [artifactPages, draftPages, outboundPages]);

  const isAnySendLogging = outboundPages.rows.some(
    (email) => amsLogPollingRemainingMs(email.amsLog?.status, email.sentAt, nowMs) > 0
  );
  const hasUnresolved = hasUnresolvedOutboxRows(rows, nowMs) || isAnySendLogging;
  const pagedPastFirst = outboundPages.rows.length > OUTBOX_PAGE_SIZE;
  const draftsPagedPastFirst = draftPages.rows.length > OUTBOX_PAGE_SIZE;

  // Refetching rewrites the cache from page one, so it stops once the reader
  // has paged further in rather than yanking the rows out from under them.
  // Advancing the clock is unconditional: it is what turns a long-unresolved
  // row from "Sending" into "Interrupted", and so what ends this interval.
  React.useEffect(() => {
    if (!hasUnresolved) {
      return;
    }
    const intervalId = window.setInterval(() => {
      setNowMs(Date.now());
      if (!pagedPastFirst) {
        void outboundPages.refresh(() => outbound.refetch());
      }
    }, OUTBOX_POLL_MS);
    return () => window.clearInterval(intervalId);
  }, [hasUnresolved, pagedPastFirst, outboundPages, outbound]);

  // A batch's emails become sends and mailbox drafts after Send returns, so its rows are read
  // again along with its summary.
  const refreshBatchRows = React.useCallback(() => {
    if (!draftsPagedPastFirst) {
      void draftPages.refresh(() => drafts.refetch());
    }
    if (!pagedPastFirst) {
      void outboundPages.refresh(() => outbound.refetch());
    }
  }, [draftsPagedPastFirst, draftPages, drafts, pagedPastFirst, outboundPages, outbound]);

  // A scheduled email coming due while the page is open moves the clock to its
  // send time, which is what starts the polling above.
  const nextSendTimeMs = nextScheduledSendTimeMs(rows, nowMs);
  React.useEffect(() => {
    if (nextSendTimeMs === null) {
      return;
    }
    let timeoutId: number | undefined;
    const waitForSendTime = () => {
      const delayMs = nextSendTimeMs - Date.now();
      // A timer can't wait longer than this, so a far-off time is waited for in steps.
      if (delayMs > LONGEST_TIMER_DELAY_MS) {
        timeoutId = window.setTimeout(waitForSendTime, LONGEST_TIMER_DELAY_MS);
        return;
      }
      // A timer can fire a moment early; the clock must still reach the send time.
      timeoutId = window.setTimeout(() => setNowMs(Math.max(Date.now(), nextSendTimeMs)), delayMs);
    };
    waitForSendTime();
    return () => window.clearTimeout(timeoutId);
  }, [nextSendTimeMs]);

  // Who owns each mailbox the reader may send from, which is what decides
  // whether a row's "Open" link would land anywhere. A read that fails leaves
  // the set empty and every link withheld: the column is a convenience, and
  // withholding it costs less than a link into a mailbox nobody is signed into.
  const { data: mailboxSharing } = useMailboxAccountSharingQuery();
  const openableAccountIds = React.useMemo(
    () => openableMailboxAccountIds(mailboxSharing?.mailboxAccounts ?? [], currentUserId),
    [mailboxSharing, currentUserId]
  );

  const scheduleBanners = React.useMemo(
    () => outboxScheduleBanners(rows, mailboxAccounts?.mailboxAccounts ?? [], nowMs),
    [rows, mailboxAccounts, nowMs]
  );
  const mailboxProviders = React.useMemo(
    () =>
      new Map(
        (mailboxAccounts?.mailboxAccounts ?? []).map((account) => [account.id, account.provider])
      ),
    [mailboxAccounts]
  );
  const mailboxAddresses = React.useMemo(
    () =>
      new Map(
        (mailboxAccounts?.mailboxAccounts ?? []).map((account) => [
          account.id,
          account.identity.email
        ])
      ),
    [mailboxAccounts]
  );

  // Read from the query's own rows, which a push's answer updates through the cache.
  const amsLogByEmailId = React.useMemo(
    () => new Map(outboundPages.rows.map((email) => [email.id, email.amsLog ?? null])),
    [outboundPages.rows]
  );
  const unreadAmsLogIds = React.useMemo(
    () =>
      new Set([
        ...unreadAmsLogEmailIds(
          outbound.error?.graphQLErrors,
          outbound.data?.searchOutboundEmails.edges
        ),
        ...unreadOnLaterPages
      ]),
    [outbound.error, outbound.data, unreadOnLaterPages]
  );
  const hasUnreadAmsLogs = unreadAmsLogIds.size > 0;
  // Null amsLogs mean no AMS logs the agency's emails, so there is no column.
  const loadedAmsLog = [...amsLogByEmailId.values()].find(Boolean);
  const hasAmsColumn = isAmsLoggingEnabled && (!!loadedAmsLog || hasUnreadAmsLogs);
  const amsColumn = React.useMemo<OutboxAmsColumn | undefined>(() => {
    if (!hasAmsColumn) return undefined;
    const amsLogOf = (row: OutboxRow) =>
      row.outbound ? (amsLogByEmailId.get(row.outbound.id) ?? null) : null;
    return {
      amsLogOf,
      isAmsLogUnread: (row) =>
        !!row.outbound && unreadAmsLogIds.has(row.outbound.id) && !amsLogOf(row),
      onPush: (row) => {
        const amsLog = amsLogOf(row);
        if (row.outbound && amsLog) setPushing({ email: row.outbound, amsLog });
      }
    };
  }, [hasAmsColumn, unreadAmsLogIds, amsLogByEmailId]);

  const columns = React.useMemo(
    () =>
      getOutboxColumns({
        nowMs,
        agencyUsers,
        checkingDraftIds: reconciliation.checkingDraftIds,
        disconnectedDraftIds: reconciliation.disconnectedDraftIds,
        onRetryUnconfirmed: setRetryUnconfirmed,
        openableMailboxAccountIds: openableAccountIds,
        unreadSources,
        mailboxProviders,
        mailboxAddresses,
        batches,
        ams: amsColumn
      }),
    [
      nowMs,
      agencyUsers,
      reconciliation.checkingDraftIds,
      reconciliation.disconnectedDraftIds,
      openableAccountIds,
      unreadSources,
      mailboxProviders,
      mailboxAddresses,
      batches,
      amsColumn
    ]
  );

  const filteredStatuses = outboxFilterStatuses(activeFilters);
  const isScheduledFilterOnly =
    activeFilters.length === 1 && filteredStatuses[0] === OutboxStatusFilterValue.Scheduled;

  return (
    <>
      <div className="mb-4 flex flex-wrap items-center gap-4">
        <div className="min-w-64 flex-1">
          <SearchFilterInput
            filterDefinitions={filterDefinitions}
            onFiltersChange={setActiveFilters}
            initialFilters={activeFilters}
            placeholder="Filter by status, subject, recipient, mailbox, person, or date..."
          />
        </div>
        <SwitchField className="w-auto gap-x-3">
          <Label>Include closed drafts</Label>
          <Switch
            name="closed"
            color="sky"
            checked={includeClosed}
            onChange={setIncludeClosed}
            aria-label="Include drafts that left the mailbox without being sent"
          />
        </SwitchField>
      </div>

      {filteredBatchQuery.error && !filteredBatch && (
        <BatchProgressLoadFailure
          className="mb-4"
          onReload={() => void filteredBatchQuery.refetch()}
        />
      )}
      <OutboxScheduleBanners banners={scheduleBanners} />
      <div className="px-4 sm:px-6 rounded-lg bg-white dark:bg-neutral-800 shadow">
        {loading && rows.length === 0 ? (
          <div className="py-12 text-center">
            <Spinner size="size-8" color="neutral" />
            <h3 className="mt-2 text-sm font-semibold text-neutral-900 dark:text-white">
              Loading outgoing email...
            </h3>
          </div>
        ) : emptyListError && rows.length === 0 ? (
          <OutboxLoadFailure error={emptyListError} onRetry={retry} />
        ) : rows.length === 0 ? (
          <div className="py-12 text-center">
            <PaperAirplaneIcon className="mx-auto size-12 text-neutral-400 dark:text-neutral-500" />
            <h3 className="mt-2 text-sm font-semibold text-neutral-900 dark:text-white">
              {filtered ? 'No email matches these filters' : 'No email here yet'}
            </h3>
            <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
              {isScheduledFilterOnly
                ? 'Nothing is scheduled. To schedule an email, use the arrow next to Send now in the composer.'
                : filtered
                  ? 'Every outgoing email was searched, not just the ones already on screen. Remove a filter to widen it.'
                  : 'Every email Cara writes for you appears here, from the moment it is composed.'}
            </p>
            {!filtered && (
              <Button color="white" className="mt-4" href="/app/settings/mailbox">
                Manage mailboxes
              </Button>
            )}
          </div>
        ) : (
          <>
            {incomplete && (
              <OutboxIncompleteNotice error={error ?? bulkSendsError} onRetry={retry} />
            )}
            {hasAmsColumn && hasUnreadAmsLogs && (
              <InlineNotice
                tone="warning"
                className="mt-4"
                title={`Couldn't load which emails were logged to ${
                  loadedAmsLog ? integrationName(loadedAmsLog.integrationType) : 'your AMS'
                }.`}
                description="Your emails are all here. Reload to try again."
                actions={
                  <Button
                    color="white"
                    disabled={outboundPages.isFetchingMore}
                    onClick={() => {
                      setUnreadOnLaterPages(new Set());
                      void outboundPages.refresh(() => outbound.refetch());
                    }}
                  >
                    Reload
                  </Button>
                }
              />
            )}
            <DataTable
              data={rows}
              columns={columns}
              keyExtractor={(row) => row.key}
              rowHref={outboxRowArtifactHref}
              tableClassName="[--gutter:theme(spacing.4)] sm:[--gutter:theme(spacing.6)]"
            />
            <OutboxLoadMoreFooter
              shown={rows.length}
              hasNextPage={hasNextPage}
              onLoadMore={loadMore}
              loading={loading}
              error={!!error}
            />
          </>
        )}
      </div>

      {sentBulkSends.map((batch) => (
        <OutboxBatchPoller key={batch.id} batch={batch} onPoll={refreshBatchRows} />
      ))}
      <UnconfirmedRetryDialog row={retryUnconfirmed} onClose={() => setRetryUnconfirmed(null)} />
      <PushEmailToAms
        email={pushing && pushableOutboundEmail(pushing.email, pushing.amsLog)}
        onAmsLogStale={() => pushing && void rereadAmsLog(pushing.email.workspaceArtifactId)}
        onClose={() => setPushing(null)}
      />
    </>
  );
};

/**
 * Reads a sent batch's progress again while it can change, which updates its row through the
 * cache, and has the page read the batch's emails again with it.
 */
const OutboxBatchPoller = ({ batch, onPoll }: { batch: OutboxBulkSend; onPoll: () => void }) => {
  const [readBatch] = useEmailBulkSendLazyQuery({
    variables: { id: batch.id },
    fetchPolicy: 'network-only'
  });
  useBatchProgressPolling(batch, () => {
    void readBatch();
    onPoll();
  });
  return null;
};

/**
 * What the page says when none of its three ledgers could be read. A server
 * Cara could not reach is offered a retry; a refusal is not, since asking again
 * would only collect it a second time, so that case points at the Inbox instead.
 */
const OutboxLoadFailure = ({ error, onRetry }: { error: ApolloError; onRetry: () => void }) => {
  const unreachable = !!error.networkError;

  return (
    <div className="py-12 text-center">
      <PaperAirplaneIcon className="mx-auto size-12 text-rose-400" />
      <h3 className="mt-2 text-sm font-semibold text-neutral-900 dark:text-white">
        Unable to load outgoing email
      </h3>
      <p className="mt-1 text-sm text-rose-600 dark:text-rose-400">
        {unreachable
          ? 'Cara could not reach the server, so none of your outgoing email could be loaded.'
          : messageFromError(error, 'The server would not read your outgoing email.')}
      </p>
      <div className="mt-4">
        {unreachable ? (
          <Button color="white" onClick={onRetry}>
            Try again
          </Button>
        ) : (
          <Button color="white" href="/app/mailbox/inbox">
            Go to Inbox
          </Button>
        )}
      </div>
    </div>
  );
};

/**
 * What the page says when one of its three ledgers could not be read at all.
 *
 * A banner as well as the per-row "Unknown" badge, because neither carries the
 * other's fact: a badge cannot say that whole emails are missing, since the
 * rows that would have said so never arrived, and the banner cannot say which
 * rows on it are affected.
 */
const OutboxIncompleteNotice = ({
  error,
  onRetry
}: {
  error: ApolloError | undefined;
  onRetry: () => void;
}) => (
  // Padding rather than margin: the card has no top padding, so a margin would fold out of it
  // and leave the notice against the card's edge.
  <div className="py-4">
    <div className="rounded-md bg-amber-50 dark:bg-amber-500/10 px-4 py-3 text-sm text-amber-800 dark:text-amber-300">
      <span className="font-medium">This list is incomplete.</span> Part of this list could not be
      loaded, so emails may be missing from it and some rows cannot say how far they got.{' '}
      <Headless.Button
        type="button"
        onClick={onRetry}
        className="font-medium underline hover:no-underline"
      >
        Try again
      </Headless.Button>
      {error && (
        <span className="mt-1 block text-xs text-amber-700 dark:text-amber-300">
          {messageFromError(error, 'Something went wrong loading your outgoing email.')}
        </span>
      )}
    </div>
  </div>
);

/**
 * The step between "Send again" and sending again, for the one failure that
 * might already be with the client.
 *
 * It confirms nothing by itself: the button leads to the composer, where the
 * send lock, the pinned version and the content-revision check still apply.
 * What this adds is the fact none of those can know — that the provider was
 * asked once already and never said what it did.
 */
export const UnconfirmedRetryDialog = (props: { row: OutboxRow | null; onClose: () => void }) => {
  const { row, onClose } = props;
  const href = row ? outboxRowArtifactHref(row) : undefined;

  return (
    <Dialog open={!!row} onClose={onClose} size="lg">
      <DialogTitle>This email may already have been sent</DialogTitle>
      <DialogBody>
        <p className="text-sm text-neutral-700 dark:text-neutral-300">
          {row && outboxUnconfirmedRetryWarning(row)}
        </p>
      </DialogBody>
      <DialogActions>
        <Button plain onClick={onClose}>
          Cancel
        </Button>
        <Button color="amber" href={href} disabled={!href}>
          Open in the composer
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/**
 * "Showing N … Load more", without the "of M" the connection footers carry.
 * The two ledgers each report their own total and one email can be counted in
 * both, so no total this page could print would be the number of rows the
 * reader would end up with.
 */
export function OutboxLoadMoreFooter(props: {
  shown: number;
  hasNextPage: boolean;
  onLoadMore: () => void;
  loading?: boolean;
  error?: boolean;
}) {
  const { shown, hasNextPage, onLoadMore, loading = false, error = false } = props;
  if (!hasNextPage) {
    return null;
  }

  return (
    <div className="px-4 py-1.5 text-center dark:border-neutral-700">
      <span className="text-xs text-neutral-500 dark:text-neutral-400">
        Showing the {shown} most recent.{' '}
        <Headless.Button
          type="button"
          onClick={onLoadMore}
          disabled={loading}
          aria-busy={loading}
          className="inline text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300 hover:underline font-medium disabled:text-neutral-400 dark:disabled:text-neutral-500 disabled:no-underline"
        >
          Load more
        </Headless.Button>
        {error && (
          <span className="ml-2 text-rose-600 dark:text-rose-400">Unable to load more.</span>
        )}
      </span>
    </div>
  );
}
